import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { Sketch } from 'vue-color';
import CircleSlider from 'circle-slider';

export default Vue.component('gradient-color-option', {
  template: require('./template.html'),
  name: 'gradient-color-option',
  computed: {
    ...mapGetters(['viewport', 'activeDroplet', 'activeDropletItem']),
    schemeColors() {
      return [{ color: '', type: 'picker' }];
    },
  },
  components: {
    'sketch-picker': Sketch,
  },
  props: [
    'option',
    'active-state',
    'dom-nodes',
    'bg-type',
    'component-config',
    'default-values',
  ],
  data() {
    return {
      pickerVals: {
        editor: null,
      },
      colorPickers: {},
      debTime: 250,
      barWidth: 290,
      gradient: null,
      activeGroupItem: null,
      activeIndex: null,
      angleSlider: null,
      typingAngle: false,
    };
  },
  watch: {
    viewport() {
      this.setInitialValue();
    },
    activeState() {
      this.setInitialValue();
    },
    activeGroupItem(item) {
      if (item) {
        this.setEditValues(item);
      }
    },
    'pickerVals.editor': function initPickerVal(active) {
      if (active) {
        this.setAngleSlider();
      }
    },
    activeDropletItem: {
      handler() {
        this.setInitialValue();
      },
      deep: true,
    },
  },
  mounted() {
    this.setInitialValue();
  },
  methods: {
    updateDroplet(params) {
      this.$emit('update-styles', {
        ...params,
        gradientVal: this.buildGradientString(this.gradient),
      });
    },
    colorPickerChange() {
      const value = this.pickerVals.editor;
      const color = this._concatPicker(value);
      this.activeGroupItem.color = color;
      this.updateDroplet(this.option.payload);
    },
    setInitialValue() {
      const option = _.cloneDeep(this.option);
      const first = option.payload.updates[0];
      let rule = first;
      let groupParent;
      if (this.areaChecked) {
        const checked = _.keys(_.pickBy(this.areas));
        rule = _.find(option.payload.updates, update => checked.includes(update.property));
      }
      let selector;
      if (this.activeDropletItem._id && this.componentConfig.itemList) {
        groupParent = first;
        const inner = this.componentConfig.itemList.selectors[rule.property];
        const nested = inner || '';
        selector = `#drp-${this.activeDropletItem._id}${nested}${this.activeState}`;
      } else {
        selector = rule.selector.replace(/\$\{state\}/gi, this.activeState);
        selector = selector.replace(/\$\{dropletId\}/gi, this.activeDroplet._id);
      }
      const property = rule.property;
      // if one area is checked, get that rule from the updates list
      const useRoot = option.$el === 'root';
      const $root = this.domNodes.$dropletRow;
      const $el = useRoot ? $root : $root.querySelectorAll(option.$el)[0];
      const string = this._getPropertyRule({
        $vm: this,
        default: this.defaultValues.gradient,
        $el,
        selector,
        property,
        groupParent,
      });
      const gradientSchema = this.getGradientSchema(string);
      this.$set(this, 'gradient', gradientSchema);
      // always set to first item on reset
      this.activeGroupItem = this.gradient.groups[0];
    },
    setEditValues(item) {
      this.pickerVals.editor = this._buildPickerVal(item.color);
    },
    getLeft(item) {
      const left = (parseInt(item.stop, 10) / 100) * this.barWidth;
      return `${left - 5}px`;
    },
    buildGradientString(gradient) {
      const type = gradient.type;
      let value = '';
      if (type === 'radial') {
        value = 'circle, ';
      } else if (type === 'linear') {
        value = `${gradient.deg}deg, `;
      }
      _.forEach(gradient.groups, (item, i) => {
        value += `${item.color} ${item.stop}%`;
        if (i !== gradient.groups.length - 1) {
          value += ', ';
        }
      });
      return `${type}-gradient(${value})`;
    },
    getGradientSchema(string) {
      let deg = 90;
      let type = 'radial';
      if (string.match(/^linear-gradient/gi)) {
        type = 'linear';
        const reduce = string.match(/-gradient\((\d+)deg,/gi)[0];
        deg = parseInt((reduce.match(/(\d+)deg/gi)[0]).replace('deg', ''), 10);
      }
      const rgba = string.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*(?:\.\d+)?))?\)\s\d+%/gi);
      const groups = rgba.map(item => ({
        color: item.split(/\s\d+%/gi)[0],
        stop: parseInt((item.match(/\d+%/gi)[0]).replace('%', ''), 10),
      }));
      return { type, deg, groups };
    },
    addGroupItem(e) {
      const percentage = this.getPercentage(e);
      const counts = _.map(this.gradient.groups, i => i.stop);

      const closest = counts.reduce((prev, curr) =>
        (Math.abs(curr - percentage) < Math.abs(prev - percentage) ? curr : prev));

      let index = _.findIndex(this.gradient.groups, item => item.stop === closest);
      const closestColor = this.gradient.groups[index].color;
      if (percentage >= closest) {
        index += 1;
      }
      this.gradient.groups.splice(index, 0, {
        stop: percentage,
        color: closestColor,
      });
      this.activeGroupItem = this.gradient.groups[index];
      this.updateDroplet(this.option.payload);
    },
    startDrag(item) {
      this.activeGroupItem = item;
      document.addEventListener('mouseup', this.endDrag, false);
      document.addEventListener('mousemove', this.onDrag, false);
    },
    onDrag(e) {
      this.activeGroupItem.stop = this.getPercentage(e);
    },
    endDrag() {
      document.removeEventListener('mousemove', this.onDrag, false);
      document.removeEventListener('mouseup', this.endDrag, false);
      this.updateDroplet(this.option.payload);
    },
    getPercentage(e) {
      const pos = e.pageX - this.$refs.sliderBar.getBoundingClientRect().left;
      let perc = Math.round(100 * (pos / this.barWidth));
      if (perc > 100) {
        perc = 100;
      }
      if (perc < 0) {
        perc = 0;
      }
      return perc;
    },
    removeItem(item, index) {
      if (this.gradient.groups.length > 2) {
        this.gradient.groups.splice(index, 1);
        this.updateDroplet(this.option.payload);
        this.activeGroupItem = this.gradient.groups[0];
        this.activeIndex = null;
      }
    },
    setAngleSlider() {
      if (!this.angleSlider) {
        this.angleSlider = true;
        setTimeout(() => {
          this.angleSlider = new CircleSlider('angle-slider', {
            snap: 45,
            clockwise: true,
            startPos: 'top',
          });
          this.angleSlider.setAngle(this.gradient.deg);
          // set all listeners
          this.angleSlider.on('sliderMove', (angle) => {
            if (!this.typingAngle) {
              this.gradient.deg = angle;
            }
          });
          this.angleSlider.on('sliderUp', () => {
            this.updateDroplet(this.option.payload);
          });
        }, 200);
      }
      if (typeof this.angleSlider.active !== 'undefined') {
        this.angleSlider.setAngle(this.gradient.deg);
      }
    },
    cleanPercent(e) {
      if (e.target.value.trim() !== '') {
        const num = parseInt(e.target.value, 10);
        if (num > 100) {
          this.activeGroupItem.stop = 100;
        } else if (num < 0 || _.isNaN(num)) {
          this.activeGroupItem.stop = 0;
        } else {
          this.activeGroupItem.stop = num;
        }
        this._debounce(() => {
          this.updateDroplet(this.option.payload);
        }, this.debTime);
      }
    },
    cleanAngle(e) {
      if (e.target.value.trim() !== '') {
        this.typingAngle = true;
        const num = parseInt(e.target.value, 10);
        if (num > 359) {
          this.gradient.deg = 359;
        } else if (num < 0 || _.isNaN(num)) {
          this.gradient.deg = 0;
        } else {
          this.gradient.deg = num;
        }
        this.angleSlider.setAngle(this.gradient.deg);
        this._debounce(() => {
          this.typingAngle = false;
          this.updateDroplet(this.option.payload);
        }, this.debTime);
      }
    },
    setGradientType(type) {
      if (this.gradient.type !== type) {
        this.gradient.type = type;
        this.updateDroplet(this.option.payload);
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/style-sidebar/options/background-options/gradient/index.js